import { mapState } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'
import { entitiesLimits } from '@/modules/limits'
import { ENTITY_TYPE } from '@/components/model/nlu-model/sidebar/entities/entity-types'
import {
  SPECIAL_CHARACTERS,
  containsSpecialCharacters
} from '@/utils/validations'
import { MlChildValidationMixin } from './ml-child-validation.mixin'

export const entityValidation = {
  mixins: [validationMixin],

  computed: {
    ...mapState({
      $allEntities: state => state.knowledgebase.entities.allEntities,
      $listEntities: state => state.knowledgebase.entities.listEntities
    }),

    entityErrors () {
      const errors = {}

      if (this.$nameError) {
        errors.name = this.$nameError
      }

      if (this.$rolesError) {
        errors.roles = this.$rolesError
      }

      if (this.$regexpErrors) {
        errors.regexPattern = this.$regexpErrors
      }

      if (this.$subListsErrors) {
        errors.subLists = this.$subListsErrors
      }

      return errors
    },

    $nameError () {
      const { name } = this.$v.localEntity
      if (name && name.$dirty) {
        if (!name.required) {
          return this.$t('validations.entity-validation.entity.required')
        }
        if (!name.unique) {
          return this.$t('validations.entity-validation.entity.unique')
        }
        if (!name.specialCharacters) {
          return this.$t('validations.entity-validation.entity.specialCharacters', { specialCharacters: SPECIAL_CHARACTERS.join(' ') })
        }
        if (!name.maxLength) {
          return this.$t('validations.entity-validation.entity.maxLength', { maxLength: entitiesLimits.simple.name.max })
        }
      }
    },

    $rolesError () {
      const { roles } = this.$v.localEntity

      if (roles && roles.$dirty) {
        if (!roles.specialCharacters) {
          return this.$t('validations.entity-validation.roles.specialCharacters', { specialCharacters: SPECIAL_CHARACTERS.join(' ') })
        }
        if (!roles.maxLength) {
          return this.$t('validations.entity-validation.roles.maxLength', { maxLength: entitiesLimits.simple.name.max })
        }
        if (!roles.unique) {
          return this.$t('validations.entity-validation.roles.unique')
        }
        if (!roles.notIncludeEmptyValues) {
          return this.$t('validations.entity-validation.roles.notIncludeEmptyValues')
        }
        if (!roles.maxAmount) {
          return this.$t('validations.entity-validation.roles.maxAmount', { maxAmount: entitiesLimits.roles.max })
        }
        if (!roles.notEqualToEntityName) {
          return this.$t('validations.entity-validation.roles.notEqualToEntityName')
        }
      }
    },

    $regexpErrors () {
      const { regexPattern } = this.$v.localEntity

      if (regexPattern && regexPattern.$dirty) {
        if (!regexPattern.required) {
          return this.$t('validations.entity-validation.regexPattern.required')
        }
      }
    },

    $subListsErrors () {
      const { subLists } = this.$v.localEntity
      if (!subLists) { return }

      return subLists.$model.map((_, index) => {
        const errors = {}
        const { canonicalForm } = subLists.$each[index]
        if (canonicalForm.$dirty) {
          if (!canonicalForm.required) {
            errors.canonicalForm = this.$t('validations.entity-validation.subLists.required')
          } else if (!canonicalForm.unique) {
            errors.canonicalForm = this.$t('validations.entity-validation.subLists.unique')
          } else if (!canonicalForm.specialCharacters) {
            errors.canonicalForm = this.$t('validations.entity-validation.subLists.specialCharacters', { specialCharacters: '*\\:<>%{}$&' })
          } else if (!canonicalForm.maxLength) {
            errors.canonicalForm = this.$t('validations.entity-validation.subLists.maxLength', { maxLength: entitiesLimits.list.name.max })
          }
        }
        return errors
      })
    },

    // --- RULES ---
    $nameRules () {
      const usedEntityNames = this.$allEntities.value
        .filter(entity => entity.id !== this.localEntity.id)
        .map(entity => entity.name.trim())

      return {
        required,
        maxLength: maxLength(entitiesLimits.list.name.max),
        specialCharacters: value => !containsSpecialCharacters(value),
        unique: name => !usedEntityNames.some(usedName => usedName === name.trim())
      }
    },

    $rolesRules () {
      const allRoles = this.$allEntities.value.reduce(
        (roles, entity) => {
          if (entity.id === this.localEntity.id) {
            return roles
          }
          return roles.concat(entity.roles || [])
        },
        []
      )

      const entityNames = this.$allEntities.value
        .filter(entity => entity.id !== this.localEntity.id)
        .map(entity => entity.name.trim())

      return {
        specialCharacters: roles => !roles.some(role => containsSpecialCharacters(role)),
        notIncludeEmptyValues: roles => !roles.some(role => !role.trim()),
        maxAmount: roles => roles.length <= entitiesLimits.roles.max,
        maxLength: roles => !roles.some(role => role.trim().length > entitiesLimits.simple.name.max),
        unique: roles => !roles.some(role => allRoles.includes(role)),
        notEqualToEntityName: (roles, { name }) => !roles.some(
          role =>
            entityNames.find(entityName => entityName === role.trim()) ||
                        role.trim() === name.trim()
        )
      }
    },

    // ML Only validation
    // TODO: rewrite it in the same manner as List Entities validation & remove ml-child-validation.mixin
    $childrenRules () {
      return {
        valid: children => {
          let valid = true
          const rootTakenNames = children.map(child => child.name)
          const stack = children.map(
            (child, i) => [child, { takenNames: [...rootTakenNames.slice(0, i), ...rootTakenNames.slice(i + 1)] }]
          )

          while (stack.length) {
            const [child, optionalFields] = stack.pop()
            const takenNames = child.children.map(child => child.name)
            stack.push(...child.children.map(
              (c, i) => [c, { takenNames: [...takenNames.slice(0, i), ...takenNames.slice(i + 1)] }]
            ))

            valid = !Object.values(MlChildValidationMixin.validations().localName)
              .some(validationFn => !validationFn(child.name, optionalFields))

            if (!valid) {
              break
            }
          }

          return valid
        }
      }
    },

    // List Only validation
    $subListRules () {
      return {
        $each: {
          canonicalForm: {
            required,
            maxLength: maxLength(entitiesLimits.list.name.max),
            specialCharacters: value => !containsSpecialCharacters(value),
            unique: (value, { usedNames }) => {
              return !usedNames || !usedNames.some(name => name === value.trim())
            }
          }
        }
      }
    },

    $validationRules () {
      switch (this.localEntity.type) {
        case (ENTITY_TYPE.regexp): {
          return {
            name: this.$nameRules,
            roles: this.$rolesRules,
            regexPattern: {
              required
            }
          }
        }

        case (ENTITY_TYPE.list): {
          return {
            name: this.$nameRules,
            roles: this.$rolesRules,
            subLists: this.$subListRules
          }
        }

        case (ENTITY_TYPE.patternAny): {
          return {
            name: this.$nameRules,
            roles: this.$rolesRules
          }
        }

        case (ENTITY_TYPE.machineLearning): {
          return {
            name: this.$nameRules,
            roles: this.$rolesRules,
            children: this.$childrenRules
          }
        }

        case (ENTITY_TYPE.prebuilt):
        default:
          return {
            roles: this.$rolesRules
          }
      }
    }
  },

  validations () {
    return {
      localEntity: this.$validationRules
    }
  }
}
